import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Product Management";
const heroImage = "https://net-basis.net/Bilder/ict-product-management-market-adaptation-modular-development.webp";
const heroAlt = "Hands present a modular cube with red blocks for ICT product management.";
const heroClaimLine1 = "Perfectly adapted!";
const heroClaimLine2 = "Safely positioned!";
const widget2 = "Your experienced partner for market-driven product management and local product adaptation in the DACH region. We support foreign manufacturers in precisely tailoring technological solutions to the requirements of the German-speaking market.";
const benefitsTitle = "How you benefit from our __Product Management__ expertise:";
const p1 = "Introducing and managing products in new markets presents companies with complex challenges. Varying regulatory requirements, local competitive situations, and differing customer needs demand a profound understanding of target markets. Without sound local expertise and experienced product management competence on the ground, companies risk misinvestments, missed market opportunities, and an inefficient use of resources.";
const p2Text = "NetBasis International therefore steps in exactly here and provides its manufacturing partners with comprehensive expertise: We take over the entire product management in the DACH region – from market analysis and product adaptation to continuous support and optimization of your product portfolio.";
const p2Parts = p2Text.split(COMPANY_NAME);
const p3 = "Based in Düsseldorf, we ensure that your products are optimally adapted to local requirements. We coordinate all product-relevant processes, monitor compliance with statutory regulations, and ensure efficient communication between your headquarters and key players in the commercial region. This positions you competitively from day one.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Professional product management without building local structures",
  "Market-driven product adaptation to local requirements and standards",
  "Continuous market observation and competitive analysis",
  "Efficient lifecycle management for your entire product portfolio",
  "Optimization of supply chains and sales processes",
  "Ensuring regulatory compliance in the DACH region",
  "Relieving your headquarters of operational product management tasks",
  "Access to local networks, service providers, and sales channels",
  "Fast responsiveness to market changes and customer feedback",
  "Scalable solution – flexibly adaptable to your growth objectives",
];

export default function ProductManagementEnPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Product Management | NetBasis International"
        description="Targeted adaptation and positioning of your products for the specific requirements of the DACH target markets."
        ogUrl="https://net-basis.net/en/services/business-development/product-management"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-center overflow-hidden pointer-events-none pt-12 sm:pt-16 md:pt-24 lg:pt-20">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center px-4 leading-[1.15] md:leading-[1.5] pm-hero-text-slide"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  <span className="block sm:inline">{heroClaimLine1}</span>
                  <span className="hidden sm:inline"> </span>
                  <span className="block sm:inline">{heroClaimLine2}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="text-foreground-950 font-semibold text-sm md:text-base mb-4 leading-relaxed">
              {widget2}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {(() => {
                  const parts = p1.split(COMPANY_NAME);
                  return parts.map((part, i) => (
                    <span key={i}>
                      {part}
                      {i < parts.length - 1 && (
                        <strong className="text-primary-600">{COMPANY_NAME}</strong>
                      )}
                    </span>
                  ));
                })()}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>{p3}</p>
            </div>

            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}